import { useTranslation, Trans } from "next-i18next";
import parse from "html-react-parser";
import CardWithIcon from "../../components/v2/cards/card-with-icon";
import {
    Docker,
    K8s, Linux, Windows
} from "../../components/v2/icons";
import AgentIntegration from "../../components/v2/agent-integration";
import Accordion from "../../components/v2/accordion/accordion";
import ProductResources from "../../components/v2/product/product-resource";
import ProductHeroSection from "../../components/v2/common/product-hero";
import ProductTour from "../../components/v2/product/product-tour";
import '../../styles/scss/_faq-section.module.scss';
import ProductOverviewSection from "../../components/v2/product-overview-section";
import { OpsAI, SessionReplay, APM } from "../../components/v2/icons-v2";
import FeaturesMonitoringV2 from "../../components/v2/tabs/features-monitoring-v2";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";
import ClientTabsTestimonials from "../../components/v2/mw-carousel/testimonial-with-tabs";
import {JavascriptLogo, ReactNativeLogo, IosLogo, AndroidLogo, FlutterLogo} from "../../components/v2/icons-v2"

export default function RUMMonitoring() {
    const { t } = useTranslation("common");

    const featuresData = t('product.rum-monitoring.features.detailedFeatures', { returnObjects: true }) as any[]
    const features = Array.isArray(featuresData) ? featuresData : []

    const faqsData = t('product.rum-monitoring.faq.questions', { returnObjects: true }) as any[]
    const faqs = Array.isArray(faqsData) ? faqsData : []

    const resourceData = t('product.rum-monitoring.resources', { returnObjects: true }) as any[]
    const resource = Array.isArray(resourceData) ? resourceData : []

    const productTourData = t('product.rum-monitoring.productTour', { returnObjects: true }) as any

    const agentIntegrationData = t('product.rum-monitoring.agentIntegration', { returnObjects: true }) as any

    const agentDeployData = t('product.shared.rumAgentDeploy', { returnObjects: true }) as any[]
    const agentDeployArray = Array.isArray(agentDeployData) ? agentDeployData : []

    const agentDeploy = agentDeployArray.map(agent => ({
        ...agent,
        icon: agent.key === 'javascript' ? <JavascriptLogo/> :
              agent.key === 'react' ? <ReactNativeLogo/> :
              agent.key === 'iOS' ? <IosLogo/> :
              agent.key === 'android' ? <AndroidLogo/> :
              agent.key === 'flutter' ? <FlutterLogo/> :
              <span className={"text-gradient"}>450+</span>
    }))

    const productOverviewData = {
            title: "Product overview",
            buttons: [
                {
                    label: "View Docs",
                    link: "https://docs.middleware.io/rum/rum-overview",
                    className: "ahref-btn primary",
                },
                {
                    label: "View Pricing",
                    link: "/pricing/",
                    className: "ahref-btn secondary",
                },
            ],
            description:
                "Middleware Real User Monitoring (RUM) offers comprehensive frontend visibility for web and mobile apps. It captures real user sessions, journeys, performance metrics such as Core Web Vitals, and frontend errors, correlating them with backend logs and traces to enable faster root-cause troubleshooting and enhance the user experience. ",
            worksWellWithTitle: "Works Well With",
            worksWellWith: [
                {
                    title: "Session Replay",
                    link: "/product/real-user-monitoring/session-replays/",
                    icon: <SessionReplay />
                },
                {
                    title: "OpsAI",
                    link: "/product/ops-ai/",
                    icon: <OpsAI />
                },
                {
                    title: "APM",
                    link: "/product/apm/",
                    icon: <APM />
                }
            ],
        };

    return (
        <>
            <ProductHeroSection title={<h1>{t('product.rum-monitoring.hero.title')} <span>{t('product.rum-monitoring.hero.titleHighlight')}</span></h1>}
                                description={t('product.rum-monitoring.hero.description')}
                                img={"/images/v2/products/real-user-monitoring.webp"} className="rum-monitoring" imgMobile={"/images/v2/products/real-user-monitoring-mobile.webp"} imgAlt={t('product.rum-monitoring.hero.imageAlt')} preloadImg hasActions={true} tips={t('product.rum-monitoring.hero.tips')}/>

            <ProductOverviewSection productOverviewData={productOverviewData} />    

            <FeaturesMonitoringV2 productFeatures={features} tourId={"rum-tour"} />

            <CallToActionDocsLink
                                        title={'Try Real User Monitoring Free and Improve User Experience.'}
                                        docsLink="https://docs.middleware.io/rum/rum-overview"
                                    />

            <section className="testimonial-section">
                <div className="container">
                    <div className="section-tag testimonial">{t("testimonials.tag")} 👤</div>
                    <h2>Hear it from the best: Why <span className="text-gradient">top companies</span> trust Middleware</h2>

                    <ClientTabsTestimonials />
                </div>
            </section>

            <ProductTour tourSrc={"https://app.supademo.com/embed/clzi8gqgy1k9b9x77igm8tifk?embed_v=2"} title={productTourData.title} tourId={"rum-tour"} description={productTourData.description} titleChanged={<h2>Take an <span className="text-gradient">Interactive Tour </span> of the Real User Monitoring</h2>}/>

            <AgentIntegration
                agentDeploy={agentDeploy}
                title={
                    <h2>
                        <Trans
                            i18nKey="product.rum-monitoring.agentIntegration.title"
                            components={{ highlight: <span className="text-gradient" /> }}
                        />
                    </h2>
                }
                description={"Integrate lightweight SDKs across JavaScript, iOS, Android, React Native, and Flutter to capture real user interactions, performance metrics, and errors in real time."}
                contactUsVariant="rum-monitoring"
            />

            <ProductResources resources={resource} title={t('product.shared.resources.title')}/>

            <section className={"faq-section"}>
                <div className={"container"}>
                    <h2>{t('product.rum-monitoring.faq.title')}</h2>
                    <p>{t('product.rum-monitoring.faq.subtitle')}</p>
                    <div className={"faq-item"}>
                        <Accordion faqs={faqs.map(faq => ({
                            ...faq,
                            answerPlain: faq.text,
                            text:
                                faq.textHtml != null && faq.textHtml !== ""
                                    ? parse(faq.textHtml)
                                    : <p>{faq.text}</p>
                        }))} showGroups={true}/>
                    </div>
                </div>
            </section>
        </>
    )
}